<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { ChartColumn, CircleAlert, Pen } from "lucide-vue-next";
  import {
    IndexTable,
    IndexTableName,
    IndexTableDetail,
    IndexTableEmpty,
  } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { StatusBadge } from "@/components/episto-ui/status-badge";
  import { Button } from "@/components/ui/button";
  import CCampaignSwitches from "@/components/MCampaign/CCampaignSwitches.vue";
  import { useCCampaignsStore } from "@/stores/cCampaigns";
  import { useMCampaignsStore } from "@/stores/mCampaigns";
  import { useFlagEmojis } from "@/utils/composables/flagEmojis";
  import { M_CAMPAIGN_STATUS_VARIANTS } from "@/components/MCampaign/mCampaignStatus";
  import type { MCampaign } from "@/types";

  const mCampaignsStore = useMCampaignsStore();
  const { mCampaigns, mCampaignsCount, currentPage, visibleCampaign, actionDropdownMCampaigns } = storeToRefs(mCampaignsStore);
  const { changePage } = mCampaignsStore;
  const { fetchCCampaigns, clearCCampaigns } = useCCampaignsStore();
  const determineFlag = useFlagEmojis();

  const campaigns = computed(() => mCampaigns.value as MCampaign[]);

  const columns: DataTableColumn<MCampaign>[] = [
    { key: "name", label: "Campaign" },
    { key: "status", label: "Status", width: 100 },
    { key: "sub_campaigns", label: "Sub-campaigns", width: 160 },
  ];

  const subCampaigns = (mCampaign: MCampaign) => {
    const all = mCampaign.c_campaigns ?? [];
    return { active: all.filter((c) => c.status === "active").length, total: all.length };
  };
  const flags = (mCampaign: MCampaign) => (mCampaign.audience_countries ?? []).slice(0, 3).map((c) => determineFlag(c.toLowerCase()));

  const expandedKeys = computed(() => {
    const open = campaigns.value[visibleCampaign.value];
    return open ? [open.token] : [];
  });

  const toggle = async (mCampaign: MCampaign) => {
    const index = campaigns.value.indexOf(mCampaign);
    if (index === visibleCampaign.value) return (visibleCampaign.value = -1);
    await clearCCampaigns();
    await fetchCCampaigns(mCampaign.token);
    visibleCampaign.value = index;
  };

  const targetsRoute = (mCampaign: MCampaign) => ({ name: "CampaignTargets", params: { token: mCampaign.token } });
  const pilotRoute = (mCampaign: MCampaign) =>
    mCampaign.last_push == null ? targetsRoute(mCampaign) : { name: "MCampaignBudgets", params: { token: mCampaign.token } };
</script>

<template>
  <IndexTable
    :items="campaigns"
    :columns="columns"
    :current-page="currentPage"
    :total="mCampaignsCount"
    :expanded-keys="expandedKeys"
    actions-class="w-[200px]"
    item-key="token"
    empty-message="No campaign yet."
    @row-click="toggle"
    @page-change="changePage">
    <template #cell-name="{ item }">
      <IndexTableName :name="item.name">
        <template #leading>
          <span
            v-if="flags(item).length"
            class="text-sm leading-none">
            {{ flags(item).join(" ") }}
          </span>
        </template>
        <span
          v-if="item.asleep"
          class="shrink-0 text-sm"
          title="Asleep">
          🌙️
        </span>
        <span
          v-if="!item.sync"
          class="flex shrink-0 items-center gap-1 text-13 text-status-warning-strong">
          <CircleAlert class="size-3.5" />
          Not synced
        </span>
      </IndexTableName>
    </template>
    <template #cell-status="{ item }">
      <StatusBadge
        :status="item.status"
        :variants="M_CAMPAIGN_STATUS_VARIANTS" />
    </template>
    <template #cell-sub_campaigns="{ item }">
      <span
        v-if="subCampaigns(item).total"
        class="text-13 text-foreground-secondary">
        {{ subCampaigns(item).active }} of {{ subCampaigns(item).total }} active
      </span>
      <IndexTableEmpty v-else />
    </template>
    <template #actions="{ item }">
      <div
        class="flex items-center justify-end gap-1"
        @click.stop>
        <RouterLink :to="{ name: 'MCampaignSettings', params: { token: item.token } }">
          <Button
            variant="ghost"
            size="sm"
            class="text-primary">
            <Pen />
            Edit
          </Button>
        </RouterLink>
        <RouterLink :to="pilotRoute(item)">
          <Button
            variant="ghost"
            size="sm"
            class="text-primary">
            <ChartColumn />
            Pilot
          </Button>
        </RouterLink>
        <ActionsDropdown
          :actions="actionDropdownMCampaigns"
          :token="item.token" />
      </div>
    </template>
    <template #expanded="{ item }">
      <IndexTableDetail bleed>
        <CCampaignSwitches
          v-if="subCampaigns(item).total"
          :m-campaign-token="item.token"
          :m-campaign-name="item.name" />
        <p
          v-else
          class="px-8 py-3 text-13 text-muted-foreground">
          No Facebook campaign yet —
          <RouterLink
            :to="targetsRoute(item)"
            class="text-primary underline-offset-2 hover:underline">
            set the targets and push
          </RouterLink>
          to create them.
        </p>
      </IndexTableDetail>
    </template>
  </IndexTable>
</template>
